import React from 'react';
import { SeatPlanItem, SeatRequestItem, scheduledSeatService } from '../../services/scheduledSeatService';
import { Customer, ScheduledRoute } from '../../types';
import { inboundInboxService } from '../../services/inboundInboxService';
import { fmtDateTime, requestDisplayStatus } from '../../components/ScheduledCommandPanel/utils';

type NotifyType = 'SUCCESS' | 'ERROR' | 'WARNING';

type Params = {
  customers: Customer[];
  routes: ScheduledRoute[];
  operatorDiscountCap: number;
  dedupedSeatPlan: SeatPlanItem[];
  operationsDayPlans: SeatPlanItem[];
  load: () => Promise<void>;
  fireNotify: (title: string, message: string, type: NotifyType) => void;
  setActiveView: React.Dispatch<React.SetStateAction<'OPERATIONS' | 'REQUESTS' | 'INVENTORY' | 'SEAT_PLAN'>>;
  setCalendarMonth: React.Dispatch<React.SetStateAction<string>>;
  setSeatPlanDate: React.Dispatch<React.SetStateAction<string>>;
  setExpandedPlanId: React.Dispatch<React.SetStateAction<string>>;
};

export const useScheduledOffersFlow = ({
  customers,
  routes,
  operatorDiscountCap,
  dedupedSeatPlan,
  operationsDayPlans,
  load,
  fireNotify,
  setActiveView,
  setCalendarMonth,
  setSeatPlanDate,
  setExpandedPlanId,
}: Params) => {
  const [reviewRequest, setReviewRequest] = React.useState<SeatRequestItem | null>(null);
  const [reviewMessage, setReviewMessage] = React.useState('');
  const [reviewSeatPriceUSD, setReviewSeatPriceUSD] = React.useState(0);
  const [reviewSeats, setReviewSeats] = React.useState(1);
  const [reviewClientName, setReviewClientName] = React.useState('');
  const [reviewClientPhone, setReviewClientPhone] = React.useState('');
  const [reviewCustomerDiscountPercent, setReviewCustomerDiscountPercent] = React.useState(0);
  const [reviewOperatorDiscountPercent, setReviewOperatorDiscountPercent] = React.useState(0);
  const [isSendingReviewOffer, setIsSendingReviewOffer] = React.useState(false);

  const focusInventoryInCalendar = (inventory: { id?: string; departureAt?: string | null } | null | undefined) => {
    const dep = String(inventory?.departureAt || '').trim();
    if (!dep) {
      fireNotify('No Matched Flight', 'No matched flight is linked to this request yet.', 'WARNING');
      return;
    }
    const depDate = new Date(dep);
    if (Number.isNaN(depDate.getTime())) {
      fireNotify('Invalid Flight Date', 'Matched flight date is invalid.', 'ERROR');
      return;
    }
    const monthIso = new Date(Date.UTC(depDate.getUTCFullYear(), depDate.getUTCMonth(), 1)).toISOString().slice(0, 10);
    const dayIso = depDate.toISOString().slice(0, 10);
    setActiveView('OPERATIONS');
    setCalendarMonth(monthIso);
    setSeatPlanDate(dayIso);
    setExpandedPlanId(String(inventory?.id || ''));
  };

  const openOfferReview = (requestRow: SeatRequestItem) => {
    const plan =
      dedupedSeatPlan.find((p) => p.id === requestRow.matchedInventory?.id) ||
      operationsDayPlans.find((p) => p.id === requestRow.matchedInventory?.id) ||
      null;
    const routeRate = routes.find(
      (r) => String(r.from || '').toUpperCase() === String(requestRow.fromCode || requestRow.matchedInventory?.fromCode || '').toUpperCase()
        && String(r.to || '').toUpperCase() === String(requestRow.toCode || requestRow.matchedInventory?.toCode || '').toUpperCase()
    );
    const seatPrice = Number(plan?.seatPriceUSD || 0) > 0
      ? Number(plan?.seatPriceUSD || 0)
      : Number(requestRow.matchedInventory?.seatPriceUSD || 0) > 0
        ? Number(requestRow.matchedInventory?.seatPriceUSD || 0)
      : Number(routeRate?.rackRateUSD || 0);
    const seats = Math.max(1, Number(requestRow.pax || 1));
    const linkedCustomer =
      customers.find((c) => c.id && c.id === (requestRow as any).customerId)
      || customers.find((c) => String(c.email || '').toLowerCase() === String(requestRow.fromEmail || '').toLowerCase())
      || customers.find((c) => String(c.name || '').toLowerCase() === String(requestRow.customerName || '').toLowerCase())
      || null;
    const customerDiscount = linkedCustomer
      ? Math.max(
          0,
          Number(
            (linkedCustomer as any).scheduleDiscount ??
            (linkedCustomer as any).scheduleRebate ??
            (linkedCustomer as any).commercialTerms?.scheduleRebate ??
            0
          )
        )
      : 0;
    const route = `${requestRow.fromCode || requestRow.matchedInventory?.fromCode || '—'} -> ${requestRow.toCode || requestRow.matchedInventory?.toCode || '—'}`;
    const dep = requestRow.matchedInventory?.departureAt
      ? fmtDateTime(requestRow.matchedInventory.departureAt)
      : (plan?.departureAt ? fmtDateTime(plan.departureAt) : 'TBD');
    setReviewRequest(requestRow);
    setReviewSeatPriceUSD(seatPrice);
    setReviewSeats(seats);
    setReviewClientName(String(linkedCustomer?.name || requestRow.customerName || requestRow.fromEmail || '').trim());
    setReviewClientPhone(String((linkedCustomer as any)?.phoneNumber || '').trim());
    setReviewCustomerDiscountPercent(customerDiscount);
    setReviewOperatorDiscountPercent(0);
    setReviewMessage(
      `Scheduled option available.\nRoute: ${route}\nDeparture: ${dep}\nSeats: ${seats}\nPrice: USD ${Math.round(seatPrice)} per seat.\n\nPlease confirm if you want us to hold and issue this booking.`
    );
  };

  const reviewPricing = React.useMemo(() => {
    const seats = Math.max(1, Number(reviewSeats || 1));
    const rackUnit = Math.max(0, Number(reviewSeatPriceUSD || 0));
    const rackTotal = rackUnit * seats;
    const customerPct = Math.max(0, Math.min(100, Number(reviewCustomerDiscountPercent || 0)));
    const operatorPct = Math.max(0, Math.min(Number(operatorDiscountCap || 0), Number(reviewOperatorDiscountPercent || 0)));
    const customerDiscountAmount = (rackTotal * customerPct) / 100;
    const operatorDiscountAmount = (rackTotal * operatorPct) / 100;
    const netTotal = Math.max(0, rackTotal - customerDiscountAmount - operatorDiscountAmount);
    const netUnit = seats > 0 ? netTotal / seats : 0;
    return {
      rackUnit,
      rackTotal,
      customerPct,
      operatorPct,
      customerDiscountAmount,
      operatorDiscountAmount,
      netTotal,
      netUnit,
    };
  }, [reviewSeatPriceUSD, reviewSeats, reviewCustomerDiscountPercent, reviewOperatorDiscountPercent, operatorDiscountCap]);

  const sendOfferFromReview = async () => {
    if (!reviewRequest) return;
    try {
      setIsSendingReviewOffer(true);
      const offerMessage = String(reviewMessage || '').trim();
      if (!offerMessage) {
        fireNotify('Offer Failed', 'Offer message is empty.', 'ERROR');
        return;
      }

      if (!String(reviewRequest.inboxMessageId || '').trim()) {
        fireNotify(
          'Send Blocked',
          'No inbound thread is linked to this request. Link to inbound email first, then send offer.',
          'ERROR'
        );
        return;
      }
      const routeFrom = reviewRequest.fromCode || reviewRequest.matchedInventory?.fromCode || '';
      const routeTo = reviewRequest.toCode || reviewRequest.matchedInventory?.toCode || '';
      const sendPayload = await scheduledSeatService.sendRequestOffer(reviewRequest.id, {
        sendMode: 'SAME_THREAD',
        subject: `Re: Scheduled availability ${routeFrom} -> ${routeTo}`.trim(),
        message: offerMessage,
        text: offerMessage,
        offeredSeats: Math.max(1, Number(reviewSeats || reviewRequest.pax || 1)),
        offeredPriceUSD: Math.max(0, Number(reviewSeatPriceUSD || 0)),
        customerName: String(reviewClientName || reviewRequest.customerName || reviewRequest.fromEmail || '').trim(),
        customerPhone: String(reviewClientPhone || '').trim(),
        customerDiscountPercent: reviewPricing.customerPct,
        operatorDiscountPercent: reviewPricing.operatorPct,
      });
      if (!String(sendPayload?.sent?.messageId || '').trim()) {
        throw new Error('Scheduled offer send did not return provider message id');
      }

      fireNotify('Offer Sent', 'Scheduled offer sent with Outbound Hub template and marked as sent.', 'SUCCESS');
      setReviewRequest(null);
      setReviewMessage('');
      await load();
    } catch (e: any) {
      fireNotify('Send Failed', e?.response?.data?.error || e?.message || 'Failed to send scheduled offer', 'ERROR');
    } finally {
      setIsSendingReviewOffer(false);
    }
  };

  const offerFromRequest = async (requestRow: SeatRequestItem) => {
    if (requestDisplayStatus(requestRow) === 'AWAITING_CLIENT') {
      fireNotify('Already Sent', 'Offer is already marked as sent for this request.', 'WARNING');
      return;
    }
    if (requestRow.matchedInventory?.id) {
      openOfferReview(requestRow);
      return;
    }
    if (requestRow.inboxMessageId) {
      try {
        const result = await inboundInboxService.matchAndOffer(requestRow.inboxMessageId);
        if (result?.matched) {
          fireNotify('Matched', `Offer prepared for ${result.route || 'route'}.`, 'SUCCESS');
          focusInventoryInCalendar({ id: result.inventoryId, departureAt: result.departureAt || null });
        } else {
          fireNotify('No Match', result?.reason || 'No matching inventory found.', 'WARNING');
        }
        await load();
        return;
      } catch (e: any) {
        fireNotify('Offer Failed', e?.response?.data?.error || e?.message || 'Failed to match and offer', 'ERROR');
        return;
      }
    }
    fireNotify(
      'Match Required',
      'No matched flight found yet. Match the request first or open Inbound and run Match & Offer.',
      'WARNING'
    );
  };

  return {
    focusInventoryInCalendar,
    isSendingReviewOffer,
    offerFromRequest,
    openOfferReview,
    reviewClientName,
    reviewClientPhone,
    reviewCustomerDiscountPercent,
    reviewMessage,
    reviewOperatorDiscountPercent,
    reviewPricing,
    reviewRequest,
    reviewSeatPriceUSD,
    reviewSeats,
    sendOfferFromReview,
    setReviewClientName,
    setReviewClientPhone,
    setReviewCustomerDiscountPercent,
    setReviewMessage,
    setReviewOperatorDiscountPercent,
    setReviewRequest,
    setReviewSeatPriceUSD,
    setReviewSeats,
  };
};
